// The solving state and how moves change it. Every input is a move; undo and redo are moves
// too, so replaying the log rebuilds the exact state, history included, on any device.
import type { Move } from '@zinc/shared';
import { hiddenGivens } from './fog';
import type { Geometry } from './geometry';

export interface CellState {
  value: string | null;
  corner: string[];
  centre: string[];
  /** Position marks (SudokuPad mode): each digit in its own spot, 1 top left to 9 bottom right. */
  position: string[];
  colours: number[];
}

export interface GridState {
  cells: CellState[];
  /** Pen lines between cell centres, as "a-b" with a < b. */
  lines: string[];
}

export interface History {
  states: GridState[];
  pointer: number;
}

const EMPTY_CELL: CellState = { value: null, corner: [], centre: [], position: [], colours: [] };

export function emptyState(g: Geometry): GridState {
  return { cells: g.cells.map(() => EMPTY_CELL), lines: [] };
}

export function current(h: History): GridState {
  return h.states[h.pointer]!;
}

export function startHistory(g: Geometry): History {
  return { states: [emptyState(g)], pointer: 0 };
}

export function canUndo(h: History): boolean {
  return h.pointer > 0;
}

export function canRedo(h: History): boolean {
  return h.pointer < h.states.length - 1;
}

/** The digits the player typed, cell by cell. */
export function typed(s: GridState): (string | null)[] {
  return s.cells.map((c) => c.value);
}

/** Whether a cell shows a given: those can't be edited. One still in the fog doesn't count. */
export function givenShows(g: Geometry, s: GridState): (i: number) => boolean {
  if (!g.fog) return (i) => g.givens[i] !== null;
  const hidden = hiddenGivens(g, typed(s));
  return (i) => g.givens[i] !== null && !hidden.has(i);
}

/** Applies one move to the history. */
export function applyMove(g: Geometry, h: History, move: Move): History {
  if (move.kind === 'undo') return canUndo(h) ? { ...h, pointer: h.pointer - 1 } : h;
  if (move.kind === 'redo') return canRedo(h) ? { ...h, pointer: h.pointer + 1 } : h;
  const next = edit(g, current(h), move);
  if (next === current(h)) return h;
  const states = h.states.slice(0, h.pointer + 1);
  states.push(next);
  return { states, pointer: states.length - 1 };
}

export function replay(g: Geometry, moves: Move[]): History {
  return moves.reduce((h, m) => applyMove(g, h, m), startHistory(g));
}

function toggleAll(list: string[][], value: string): (cur: string[]) => string[] {
  const allHave = list.length > 0 && list.every((l) => l.includes(value));
  return (cur) =>
    allHave ? cur.filter((v) => v !== value) : cur.includes(value) ? cur : [...cur, value];
}

function sortMarks(g: Geometry, marks: string[]): string[] {
  return [...marks].sort((a, b) => g.digits.indexOf(a) - g.digits.indexOf(b));
}

/** The grid after a single edit. Returns the same object when nothing changes. */
export function edit(g: Geometry, s: GridState, move: Move): GridState {
  const shows = givenShows(g, s);
  const editable = move.cells.filter((i) => i >= 0 && i < g.cells.length && !shows(i));
  const value = move.value === undefined || move.value === null ? null : String(move.value);
  let changed = false;
  const cells = s.cells.slice();
  const set = (i: number, cell: CellState) => {
    cells[i] = cell;
    changed = true;
  };

  switch (move.kind) {
    case 'digit': {
      if (value === null || !g.digits.includes(value)) return s;
      const allHave = editable.length > 0 && editable.every((i) => s.cells[i]!.value === value);
      for (const i of editable) set(i, { ...s.cells[i]!, value: allHave ? null : value });
      if (!allHave && move.auto) {
        // "Clear notes when a digit is placed": the digit leaves its peers' marks.
        for (const i of editable) {
          for (const p of g.peers[i]!) {
            const c = cells[p]!;
            if (
              c.corner.includes(value) ||
              c.centre.includes(value) ||
              c.position.includes(value)
            ) {
              set(p, {
                ...c,
                corner: c.corner.filter((v) => v !== value),
                centre: c.centre.filter((v) => v !== value),
                position: c.position.filter((v) => v !== value),
              });
            }
          }
        }
      }
      break;
    }
    case 'corner':
    case 'pencil':
    case 'centre':
    case 'position': {
      if (value === null || !g.digits.includes(value)) return s;
      const key =
        move.kind === 'centre' ? 'centre' : move.kind === 'position' ? 'position' : 'corner';
      const targets = editable.filter((i) => s.cells[i]!.value === null);
      const toggle = toggleAll(
        targets.map((i) => s.cells[i]![key]),
        value,
      );
      for (const i of targets)
        set(i, { ...s.cells[i]!, [key]: sortMarks(g, toggle(s.cells[i]![key])) });
      break;
    }
    case 'colour': {
      const k = Number(move.value);
      const targets = move.cells.filter((i) => i >= 0 && i < g.cells.length);
      if (!Number.isInteger(k) || k < 0 || k > 9) return s;
      if (k === 0) {
        for (const i of targets)
          if (s.cells[i]!.colours.length) set(i, { ...s.cells[i]!, colours: [] });
        break;
      }
      const allHave = targets.length > 0 && targets.every((i) => s.cells[i]!.colours.includes(k));
      for (const i of targets) {
        const cur = s.cells[i]!.colours;
        const colours = allHave
          ? cur.filter((c) => c !== k)
          : cur.includes(k)
            ? cur
            : [...cur, k].sort((a, b) => a - b);
        set(i, { ...s.cells[i]!, colours });
      }
      break;
    }
    case 'delete': {
      const targets = move.cells.filter((i) => i >= 0 && i < g.cells.length);
      const layer = value; // 'colour' clears colours; anything else clears top-down
      if (layer === 'colour') {
        for (const i of targets)
          if (s.cells[i]!.colours.length) set(i, { ...s.cells[i]!, colours: [] });
        break;
      }
      const ed = targets.filter((i) => !shows(i));
      if (ed.some((i) => s.cells[i]!.value !== null)) {
        for (const i of ed) if (s.cells[i]!.value !== null) set(i, { ...s.cells[i]!, value: null });
      } else if (
        ed.some(
          (i) =>
            s.cells[i]!.corner.length || s.cells[i]!.centre.length || s.cells[i]!.position.length,
        )
      ) {
        for (const i of ed) set(i, { ...s.cells[i]!, corner: [], centre: [], position: [] });
      } else {
        for (const i of targets)
          if (s.cells[i]!.colours.length) set(i, { ...s.cells[i]!, colours: [] });
      }
      break;
    }
    case 'pen': {
      if (move.cells.length !== 2) return s;
      const [a, b] = [...move.cells].sort((x, y) => x - y) as [number, number];
      const key = `${a}-${b}`;
      const lines = s.lines.includes(key) ? s.lines.filter((l) => l !== key) : [...s.lines, key];
      return { cells: s.cells, lines };
    }
    case 'fill': {
      // "Fill all candidates": every digit as marks in the selected empty cells; pressed again
      // (every one of them already full), it takes those marks off.
      const key = move.markKey ?? 'centre';
      const all = g.digits.join();
      const targets = editable.filter((i) => s.cells[i]!.value === null);
      const full = targets.length > 0 && targets.every((i) => s.cells[i]![key].join() === all);
      for (const i of targets) {
        const c = s.cells[i]!;
        if (full) set(i, { ...c, [key]: [] });
        else if (c[key].join() !== all) set(i, { ...c, [key]: [...g.digits] });
      }
      break;
    }
    case 'clear':
      return { cells: s.cells.map(() => EMPTY_CELL), lines: [] };
    case 'nudge': {
      const ok = (i: number) => i >= 0 && i < g.cells.length && !shows(i);
      for (const i of move.clear ?? [])
        if (ok(i) && cells[i]!.value !== null) set(i, { ...cells[i]!, value: null });
      for (const [i, d] of move.place ?? [])
        if (ok(i) && g.digits.includes(d) && cells[i]!.value !== d)
          set(i, { ...cells[i]!, value: d });
      const key = move.markKey ?? 'centre';
      for (const [i, list] of move.marks ?? []) {
        if (!ok(i) || cells[i]!.value !== null) continue;
        const marks = sortMarks(
          g,
          list.filter((d) => g.digits.includes(d)),
        );
        if (marks.join() !== cells[i]![key].join()) set(i, { ...cells[i]!, [key]: marks });
      }
      break;
    }
    default:
      return s;
  }
  return changed ? { cells, lines: s.lines } : s;
}

/** Each cell's digit as the player sees it: a given that shows, or what they typed. */
export function values(g: Geometry, s: GridState): (string | null)[] {
  const shows = givenShows(g, s);
  return g.cells.map((_, i) => (shows(i) ? g.givens[i]! : s.cells[i]!.value));
}

/** Empty cells whose marks (any kind) hold the digit (the matching-candidates highlight). */
export function matchingCandidates(
  s: GridState,
  vals: readonly (string | null)[],
  digit: string,
): Set<number> {
  const out = new Set<number>();
  s.cells.forEach((c, i) => {
    if (
      !vals[i] &&
      (c.corner.includes(digit) || c.centre.includes(digit) || c.position.includes(digit))
    )
      out.add(i);
  });
  return out;
}

export function isFull(g: Geometry, s: GridState): boolean {
  return values(g, s).every((v) => v !== null);
}

/** Cells whose entered digit differs from the solution. */
export function wrongCells(g: Geometry, s: GridState): number[] {
  if (!g.solution) return [];
  const shows = givenShows(g, s);
  const out: number[] = [];
  s.cells.forEach((c, i) => {
    if (c.value !== null && !shows(i) && c.value !== g.solution![i]) out.push(i);
  });
  return out;
}

/** Repeated digits in a house (rows, columns, regions): a fast local check. */
export function houseConflicts(g: Geometry, s: GridState): number[] {
  const vals = values(g, s);
  const bad = new Set<number>();
  for (const h of g.houses) {
    const seen = new Map<string, number>();
    for (const i of h) {
      const v = vals[i];
      if (v === null || v === undefined) continue;
      const first = seen.get(v);
      if (first !== undefined) {
        bad.add(first);
        bad.add(i);
      } else seen.set(v, i);
    }
  }
  return [...bad];
}
